
import {
  ErrorType,
  FuelStop,
  SuggestedFuelStop,
  SuggestedFuelStopTrigger, Tag,
  Trip,
  TripError,
  TripLog,
} from '@/generated-clients/myTrips'
import DateField from '@components/my-trips/trips-overview/DateField.vue';
import FuelStopField from '@components/my-trips/trips-overview/FuelStopField.vue';
import SuggestedFuelStopField from '@components/my-trips/trips-overview/SuggestedFuelStopField.vue';
import TripAddButton from '@components/my-trips/trips-overview/TripAddButton.vue';
import TripField from '@components/my-trips/trips-overview/TripField.vue';
import TripMergeButton from '@components/my-trips/trips-overview/TripMergeButton.vue';
import TripMissingField from '@components/my-trips/trips-overview/TripMissingField.vue';
import UpsellingBanner from '@components/my-trips/trips-overview/UpsellingBanner.vue';
import AppListItemPlaceholder from '@components/new-styleguide/AppListItemPlaceholder.vue';
import DeviceHelpSection from '@components/shared/section/DeviceHelpSection.vue';
import NoTripsSection from '@components/shared/section/NoTripsSection.vue';
import RestartSetupSection from '@components/shared/section/RestartSetupSection.vue';
import ArchivedTripSection from '@components/my-trips/trips-overview/ArchivedTripSection.vue';
import storageService from '@util/storageService';
import { FilterOptions, LoadState } from '@util/types';
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator'
import { namespace } from 'vuex-class';
import { parse } from "@util/dates-and-times/duration";

const TriplogModule = namespace('Triplog');

const SettingsModule = namespace('Settings');

@Component({
  name: 'TripsOverview',
  components: {
    ArchivedTripSection,
    SuggestedFuelStopField,
    AppListItemPlaceholder,
    TripAddButton,
    DateField,
    TripField,
    TripMissingField,
    TripMergeButton,
    FuelStopField,
    RestartSetupSection,
    DeviceHelpSection,
    NoTripsSection,
    UpsellingBanner
  }
})
export default class TripsOverview extends Vue {
  @TriplogModule.Mutation('RESET_GET_TRIPS')
  private resetGetTrips;

  @TriplogModule.Mutation('RESET_TRIP_ERRORS')
  private resetTripErrors;

  @TriplogModule.Getter('getVehicles')
  private triplogs: TripLog[];

  @TriplogModule.Action('mergeTrips')
  private mergeTripsRequest;

  @TriplogModule.Getter('getTripsStatus')
  private loadTripsState;

  @TriplogModule.Getter('getMergeTripStatus')
  private mergeTripStatus;

  @TriplogModule.Getter('getTripErrorsStatus')
  private loadTripErrorsState;

  @TriplogModule.Getter('getVehicleSummaryStatus')
  private vehicleSummaryStatus;

  @TriplogModule.Getter('getSelectedTriplog')
  private triplog: TripLog;

  @TriplogModule.Getter('getTripsAreMoreAvailable')
  private areMoreTripsAvailable;

  @TriplogModule.Action('hideSuggestedFuelStop')
  private hideSuggestedFuelStop;

  @SettingsModule.Getter('getQuickActionTags')
  private quickActionTags: Tag[];

  @Prop(Object)
  private filterOptions: FilterOptions;

  @Prop()
  private trips: Trip[]

  @Prop()
  private suggestedFuelStops: SuggestedFuelStop[];

  @Prop()
  private tripErrors: TripError[]

  @Prop({ required: false })
  private latestTrip: Trip;

  @Prop({ default: false })
  private loading: boolean;

  private footerOffset = 575;

  private atBottom = true;

  private quickActionsOpened = {};

  async created() {
    window.addEventListener('scroll', this.checkAndLoadAdditionalTrips);
  }

  private destroyed() {
    window.removeEventListener('scroll', this.checkAndLoadAdditionalTrips);
  }

  private checkAndLoadAdditionalTrips() {
    const bottomOfWindow = document.documentElement.scrollTop + window.innerHeight >= document.documentElement.offsetHeight - this.footerOffset;

    if (bottomOfWindow && !this.atBottom) {
      this.atBottom = true;
      this.$emit('bottomScroll');
    } else if (!bottomOfWindow) {
      this.atBottom = false;
    }
  }

  private nextTripErrorsForTrip(trip: Trip) {
    if (this.tripErrors) {
      return this.tripErrors.filter(te => te.type !== ErrorType.GapOdometer && te.second.id === trip.id);
    }

    return [];
  }

  private previousTripErrorsForTrip(trip: Trip) {
    if (this.tripErrors) {
      return this.tripErrors.filter(te => te.type !== ErrorType.GapOdometer && te.first.id === trip.id);
    }

    return [];
  }

  private getSuggestedFuelStopAfterTrip(trip: Trip) {
    if (trip.endInfo) {
      return this.suggestedFuelStops.find(stop => stop.trigger !== SuggestedFuelStopTrigger.AVERAGEDISTANCE && stop.timestamp === trip.endInfo.timestamp);
    }
  }

  private getSuggestedFuelStopBeforeTrip(trip: Trip) {
    if (trip.startInfo) {
      return this.suggestedFuelStops.find(stop => stop.trigger !== SuggestedFuelStopTrigger.AVERAGEDISTANCE && stop.timestamp === trip.startInfo.timestamp);
    }
  }

  private showSuggestedFuelStopBeforeTrip(trip: Trip, index: number) {
    return this.getSuggestedFuelStopBeforeTrip(trip) &&
      (index + 1 >= this.trips.length || !this.getSuggestedFuelStopAfterTrip(this.trips[index + 1]));
  }

  private showSuggestedFuelStopAfterTrip(trip: Trip) {
    const noFuelStopsDone = this.fuelStopsDuringOrAfterTrip(trip).length == 0
    return noFuelStopsDone && this.getSuggestedFuelStopAfterTrip(trip)
  }

  private isMissingKilometres(key) {
    if ((key + 1) >= this.trips.length) {
      return false;
    }

    return this.tripErrors.some(te => te.type === ErrorType.GapOdometer &&
      ((te.first.id === this.trips[key].id && te.second.id === this.trips[key + 1].id) ||
      (te.first.id === this.trips[key + 1].id && te.second.id === this.trips[key].id)));
  }

  private isMergable(key): boolean {
    if ((key + 1) >= this.trips.length || this.isMissingKilometres(key) || this.filterOptions.errorsOnly) {
      return false;
    }

    const thisTrip = this.trips[key];
    const previousTrip = this.trips[key + 1];

    if (previousTrip.fuelStops.length > 0 || thisTrip.fsl || previousTrip.fsl || !thisTrip.endInfo || !previousTrip.endInfo) {
      return false;
    }

    const startDate = new Date(thisTrip.startInfo.timestamp)
    const prevStartDate = new Date(previousTrip.startInfo.timestamp)
    const endDate = new Date(previousTrip.endInfo.timestamp)

    const timeGapInMillis = startDate.getTime() - endDate.getTime();
    const timeGapInMinutes = Math.round((timeGapInMillis / 1000) / 60);

    return timeGapInMinutes <= this.mergeBreakThreshold && startDate.toLocaleDateString() === prevStartDate.toLocaleDateString();
  }

  private mergeTrip(key) {
    this.mergeTripsRequest({
      firstTripId: this.trips[key].id,
      secondTripId: this.trips[key + 1].id,
      vehicleId: this.vehicleId
    })
  }

  private shouldDisplayTripDate(key) {
    const trip = this.trips[key];
    const fuelStopAbove = trip.fuelStops[0]

    if (fuelStopAbove) {
      return !this.isSameDateValues(trip.startInfo.timestamp, fuelStopAbove.timestamp)
    }

    if (key === 0) {
      return true;
    }

    const tripAbove = this.trips[key - 1]
    return !this.isSameDateValues(trip.startInfo.timestamp, tripAbove.startInfo.timestamp);
  }

  private shouldDisplayFuelStopDate(tripKey: number, fuelStopKey: number) {
    const trip = this.trips[tripKey];
    const fuelStops = [...trip.fuelStops].reverse();
    const fuelStop = fuelStops[fuelStopKey]
    const isTopFuelStop = fuelStopKey === 0;

    if (isTopFuelStop && tripKey === 0) {
      return true;
    }

    if (isTopFuelStop) {
      const tripAbove = this.trips[tripKey - 1];
      return !this.isSameDateValues(fuelStop.timestamp, tripAbove.startInfo.timestamp);
    }

    const fuelStopAbove = fuelStops[fuelStopKey - 1];
    return !this.isSameDateValues(fuelStop.timestamp, fuelStopAbove.timestamp);
  }

  private isSameDateValues(dateStr, otherStr) {
    const date = new Date(dateStr);
    const other = new Date(otherStr);

    return date.getFullYear() === other.getFullYear()
      && date.getMonth() === other.getMonth()
      && date.getDate() === other.getDate();
  }

  private fuelStopsDuringOrAfterTrip(trip: Trip): FuelStop[] {
    if (this.filterOptions.errorsOnly) {
      return []
    }

    return (trip.fuelStops || [])
      .filter(fs => fs.timestamp > trip.startInfo.timestamp)
      .reverse();
  }

  private fuelStopBeforeTrip(trip: Trip) {
    return trip.fuelStops.find(fs => fs.timestamp <= trip.startInfo.timestamp);
  }

  private odometerStart(index: number) {
    if (this.trips[index + 1].endInfo) {
      return this.trips[index + 1].endInfo.odometer;
    }
    return this.trips[index + 1].startInfo.odometer;
  }

  private prevTripId(index: number) {
    if (this.trips[index + 1] && this.trips[index + 1].id) {
      return this.trips[index + 1].id;
    }
    return null;
  }

  private handleAddTrip() {
    this.$router.push({
      name: 'myTripsAdd',
      query: { vehicle: this.vehicleId.toString() }
    })
  }

  private handleAddFuelStop() {
    this.$router.push({
      name: 'fuelStopAdd',
      query: { vehicle: this.vehicleId.toString(), prevTripId: this.latestTrip ? this.latestTrip.id.toString() : undefined }
    })
  }

  private closeSuggestedFuelStop(trip?: Trip, position?: 'after' | 'before') {
    let date;
    if (trip && position === 'after') {
      date = this.getSuggestedFuelStopAfterTrip(trip).timestamp
    } else if (trip && position === 'before') {
      date = this.getSuggestedFuelStopBeforeTrip(trip).timestamp
    }

    this.hideSuggestedFuelStop({
      vehicleId: this.vehicleId,
      timestamp: date ? new Date(date).toISOString() : null
    });
  }

  get mergeBreakThreshold() {
    return parse(this.triplog.vehicleSettings.mergeBreakThreshold);
  }

  get stickySuggestedFuelStop(): SuggestedFuelStop {
    return this.suggestedFuelStops.find(s => s.trigger === SuggestedFuelStopTrigger.AVERAGEDISTANCE);
  }

  get hasNoTrips() {
    return this.trips.length === 0 && !this.areFiltersActive;
  }

  get showRestartSetup() {
    return this.triplog && this.triplog.vehicle.deviceId && this.triplog.vehicle.setupDone === false;
  }

  get placeholdersAmount() {
    return 8;
  }

  get additionalTripsLoadingAmount() {
    return this.isLoadingAdditionalTrips ? 10 : 0;
  }

  get showStickySuggestedFuelStop() {
    return this.stickySuggestedFuelStop; // API for hiding suggestions CC-3080
  }

  get smartConnected() {
    return this.triplog && this.triplog.smartConnected;
  }

  get vehicleId() {
    return this.triplog && this.triplog.vehicle.id;
  }

  get showAddButton() {
    return this.triplog && !this.triplog.archived && this.vehicleId && this.trips && this.trips.length > 0;
  }

  get areCategoryFiltersActive() {
    return this.filterOptions.categoryFiltersActive;
  }

  get areFiltersActive() {
    return this.filterOptions.filtersActive;
  }

  get areTripsContiguous() {
    return !this.areCategoryFiltersActive && this.vehicleId;
  }

  get isLoadingFirstTrips() {
    return (this.loadTripsState === LoadState.Loading
    || this.loadTripsState === LoadState.None
    || this.loadTripErrorsState === LoadState.Loading
    ) && this.trips.length === 0;
  }

  get isLoadingAdditionalTrips() {
    return this.loadTripsState === LoadState.Loading && this.trips.length > 0;
  }

  get hasLoadingFailed() {
    return [LoadState.Error, LoadState.Unauthorized].includes(this.loadTripsState)
  }

  get isMerging() {
    return this.mergeTripStatus === LoadState.Loading;
  }

  handleSwipeRight(index) {
    this.quickActionsOpened = Object.assign({}, this.trips.map(_ => false), { [index]: true });
  }

  handleSwipeLeft() {
    this.quickActionsOpened = Object.assign({}, this.trips.map(_ => false));
  }

  get isArchived() {
    return this.triplog && this.triplog.archived;
  }
}
